/**
 * Engine data model. All scores are FIXED-POINT integers in per-mille (0..1000) — no
 * floats ever reach a graded output, so the browser and the Worker agree byte-for-byte
 * (BUILD_PLAN §A5). Time is always a caller-provided `now` (ms); engines never read the
 * clock.
 */

import type { MisconceptionTag, ResultStatus, ReasoningSkill } from '@usacow/shared';

/** Integer 0..1000. 1000 = 100%. */
export type PerMille = number;

export const PM = {
  ZERO: 0,
  FULL: 1000,
  /** Clamp to [0,1000] and round to integer — the only place rounding happens. */
  clamp(n: number): PerMille {
    return Math.max(0, Math.min(1000, Math.round(n)));
  },
} as const;

/** Outcome of grading one interaction. */
export interface GradeResult {
  readonly status: ResultStatus;
  readonly score: PerMille;
  readonly correctComponents: readonly string[];
  readonly missingComponents: readonly string[];
  readonly misconceptionTags: readonly MisconceptionTag[];
}

/** A single behavioral signal feeding mastery (one interaction's outcome). */
export interface AttemptSignal {
  readonly skill: ReasoningSkill;
  readonly topic: string;
  readonly difficulty: PerMille; // scenario difficulty as per-mille
  readonly score: PerMille;
  readonly latencyMs: number;
  readonly hintsUsed: number;
  readonly isTransfer: boolean; // unseen isomorphic/near-miss variant
  readonly now: number;
}

/** Mastery estimate at the intersection of topic × skill, with confidence (PRD §9.1). */
export interface SkillEstimate {
  readonly skill: ReasoningSkill;
  readonly topic: string;
  readonly score: PerMille; // competence
  readonly confidence: PerMille; // how much evidence backs it
  readonly attempts: number;
  readonly lastPracticedAt: number; // ms
  readonly retentionRisk: PerMille; // higher = more likely to be forgotten now
}

/** A spaced-repetition card's scheduling state (SM-2-style, integer). */
export interface ReviewItem {
  readonly id: string;
  readonly skillTags: readonly string[];
  readonly misconceptionTags: readonly MisconceptionTag[];
  readonly intervalDays: number;
  readonly ease: PerMille; // ease factor ×1000 (1300..2500), 2500 = "2.5"
  readonly reps: number;
  readonly dueAt: number; // ms
  readonly lastReviewedAt: number; // ms
}

/** How well the student recalled a card (maps to SR grade). */
export type RecallGrade = 'again' | 'hard' | 'good' | 'easy';

export type { MisconceptionTag, ResultStatus, ReasoningSkill };
